import { useState } from "react";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import { fetchRaw } from "../../../api/client.js";
import styles from "./CrmWebhook.module.css";

// Working example: Subscription (Standard) — France. productCode is the CRM package id
// (resolved via the crm_package_mappings table), NOT the internal package code. Region comes
// from metadata.regionName (falls back to metadata.publication). Replace firmId with a real
// CRM firm entity id.
const SAMPLE_PAYLOAD = `{
  "correlationId": "dev-test-standard-france",
  "firmId": "PASTE_CRM_FIRM_ENTITY_ID",
  "productCode": "73f5d6b0-9b34-f011-8c4c-0022481ac898",
  "metadata": {
    "regionName": "FRANCE",
    "publication": "The Legal 500: Europe, Middle East & Africa"
  },
  "suggestedFirmName": "Example Firm",
  "contactName": "Jane Doe",
  "contactEmail": "jane@example.com",
  "startDate": "2026-07-04",
  "endDate": "2027-07-04"
}`;

type WebhookResult = {
  status: number;
  ok: boolean;
  body: unknown;
};

function formatResultBody(body: unknown): string {
  if (typeof body === "string") {
    return body;
  }
  return JSON.stringify(body, null, 2);
}

function parseJsonOrKeepRawText(rawText: string): unknown {
  if (!rawText) {
    return null;
  }
  try {
    return JSON.parse(rawText);
  } catch {
    return rawText;
  }
}

function CrmWebhookPage() {
  const { hasPermission } = usePermissions();
  const canSend = hasPermission("developer.write");

  const [payloadText, setPayloadText] = useState(SAMPLE_PAYLOAD);
  const [parseError, setParseError] = useState<string | null>(null);
  const [result, setResult] = useState<WebhookResult | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  async function handleSend() {
    setParseError(null);
    setRequestError(null);
    setResult(null);

    let parsedPayload: unknown;
    try {
      parsedPayload = JSON.parse(payloadText);
    } catch (error) {
      setParseError(error instanceof Error ? error.message : "Invalid JSON");
      return;
    }

    setIsSending(true);
    try {
      const response = await fetchRaw("/v1/developer/crm-webhook/subscription-created", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(parsedPayload),
      });
      const rawResponseText = await response.text();
      const responseBody = parseJsonOrKeepRawText(rawResponseText);
      setResult({ status: response.status, ok: response.ok, body: responseBody });
    } catch (error) {
      setRequestError(
        error instanceof Error ? error.message : "Failed to reach the CRM webhook endpoint",
      );
    } finally {
      setIsSending(false);
    }
  }

  if (!canSend) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="CRM Subscription Webhook Tester" />
        <div className={styles.accessDenied} role="alert">
          <p>You do not have permission to use the CRM Webhook Tester.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader title="CRM Subscription Webhook Tester" />

      <p className={styles.explainer}>
        Posts the JSON below to the CRM subscription-created webhook (via staff-bff, which injects
        the webhook secret). Firm is resolved by CRM entity number; a contact email provisions a
        firm-admin user.
      </p>

      <div className={styles.formSection}>
        <textarea
          className={styles.textarea}
          rows={14}
          spellCheck={false}
          value={payloadText}
          onChange={event => setPayloadText(event.target.value)}
          aria-label="CRM subscription-created webhook payload"
        />

        {parseError && <div className={styles.parseError}>Invalid JSON: {parseError}</div>}

        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.sendButton}
            disabled={isSending}
            onClick={() => void handleSend()}
          >
            {isSending ? "Sending..." : "Send webhook"}
          </button>
        </div>
      </div>

      {requestError && <div className={styles.parseError}>{requestError}</div>}

      {result && (
        <div className={styles.resultSection}>
          <h2 className={styles.resultHeading}>Response</h2>
          <span
            className={`${styles.statusBadge} ${result.ok ? styles.statusSuccess : styles.statusError}`}
          >
            HTTP {result.status}
          </span>
          <pre className={styles.resultBody}>{formatResultBody(result.body)}</pre>
        </div>
      )}
    </div>
  );
}

export default CrmWebhookPage;
